import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { SEO_SITE, getSeoEntry } from '@src/configs/seo.config'

interface PropsType {
	/** 지정하면 seo.config 의 값 대신 이걸 쓴다 (동적 페이지용) */
	title?: string
	description?: string
	ogImage?: string
	noindex?: boolean
	/** 페이지 고유 구조화 데이터. Product, ItemList 등 */
	jsonLd?: object | object[]
}

/**
 * 페이지별 메타 태그를 주입한다.
 *
 * HomeLayout 에 한 번 마운트해두면 라우트가 바뀔 때마다 현재 경로의 메타로 갱신된다.
 * 개별 페이지에서 동적인 값(제품 가격 등)을 얹고 싶으면 그 페이지에서 다시 한 번
 * 렌더한다 — react-helmet-async 는 나중에 마운트된 쪽이 이긴다.
 *
 * canonical 이 특히 중요하다. 예전에는 index.html 에 `https://judahs.io` 가
 * 하드코딩돼 있어서 모든 페이지가 홈을 정본으로 지목했고, 그 탓에 개별 페이지가
 * 색인되지 않았다.
 */
export const Seo = (props: PropsType) => {
	const location = useLocation()
	const entry = getSeoEntry(location.pathname)

	const title = props.title ?? entry.title
	const description = props.description ?? entry.description
	const ogImage = props.ogImage ?? entry.ogImage ?? SEO_SITE.defaultOgImage
	const noindex = props.noindex ?? entry.noindex ?? false
	const canonical = `${SEO_SITE.origin}${entry.path === '/' ? '' : entry.path}`

	const jsonLdList: object[] =
		props.jsonLd === undefined
			? []
			: Array.isArray(props.jsonLd)
			? props.jsonLd
			: [props.jsonLd]

	return (
		<Helmet prioritizeSeoTags>
			<title>{title}</title>
			<meta name="description" content={description} />

			<meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />

			{/*
				색인하지 않을 페이지에는 canonical 을 붙이지 않는다. 정본을 지정할 이유가
				없을뿐더러, 존재하지 않는 경로에서는 그 경로가 그대로 canonical 에 박힌다.
			*/}
			{noindex ? null : <link rel="canonical" href={canonical} />}

			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={canonical} />
			<meta property="og:image" content={ogImage} />
			<meta property="og:type" content="website" />
			<meta property="og:site_name" content={SEO_SITE.siteName} />
			<meta property="og:locale" content={SEO_SITE.locale} />

			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={title} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={ogImage} />

			{jsonLdList.map((aJsonLd, idx) => (
				<script type="application/ld+json" key={`jsonLd-${idx}`}>
					{JSON.stringify(aJsonLd)}
				</script>
			))}
		</Helmet>
	)
}
